import React, { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTitle } from "../lib/title";
import { graphql } from "babel-plugin-relay/macro";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import { DateRangeDisplay } from "../components/DateRange";
import { SymmetricBar } from "../components/utils";
import { useStrictURLDaterange, withDateRange } from "../lib/urlParams";
import { DateRange } from "../lib/dateUtils";
import { format } from "date-fns";
import APIOverviewQueryGraphql, {
    APIOverviewQuery,
} from "./__generated__/APIOverviewQuery.graphql";
import styles from "./APIOverview.module.scss";

export function useDataReloadOnDateChange(
    loadData: (dateRange: DateRange) => void,
) {
    const [urlDateRange] = useStrictURLDaterange();

    useEffect(() => {
        loadData(urlDateRange);
    }, [loadData, urlDateRange]);
}

function _APIOverview() {
    useTitle("API Overview");

    const [dataQueryRef, loadDataQuery] = useQueryLoader<APIOverviewQuery>(
        APIOverviewQueryGraphql,
    );

    const loadData = useCallback(
        (dateRange: DateRange) => {
            loadDataQuery({
                dateRange: {
                    start: format(dateRange.start, "yyyy-MM-dd"),
                    end: format(dateRange.end, "yyyy-MM-dd"),
                },
            });
        },
        [loadDataQuery],
    );

    useDataReloadOnDateChange(loadData);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>API Overview</h1>
            </div>
            <SymmetricBar middle={<DateRangeDisplay />} />
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>Customer Name</th>
                        <th>Total API Requests</th>
                    </tr>
                </thead>
                <tbody>
                    {dataQueryRef && (
                        <APIOverviewData queryRef={dataQueryRef} />
                    )}
                </tbody>
            </table>
        </div>
    );
}

export const APIOverview = withDateRange(_APIOverview, 7);

function APIOverviewData(props: {
    queryRef: PreloadedQuery<APIOverviewQuery>;
}) {
    const response = usePreloadedQuery<APIOverviewQuery>(
        graphql`
            query APIOverviewQuery($dateRange: DateRangeInput!) {
                apiStats(dateRange: $dateRange) {
                    customerID
                    CustomerName
                    totalCount
                }
            }
        `,
        props.queryRef,
    );

    return (
        <>
            {response.apiStats.map((stat) => (
                <tr key={stat.customerID}>
                    <td>
                        <Link
                            to={{
                                pathname: `${stat.customerID}`,
                                search: window.location.search,
                            }}
                        >
                            {stat.CustomerName}
                        </Link>
                    </td>
                    <td>{stat.totalCount}</td>
                </tr>
            ))}
        </>
    );
}
